import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { utils, write } from 'xlsx';
import { ToastContainer, toast } from 'react-toastify';
import useHandleErrors from '../../utils/user.utils';

const EmployeeSeparation = () => {
    const [employees, setEmployees] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [modalData, setModalData] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const handleErrors = useHandleErrors();
    const [loading, setLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState('');
    const [comment, setComment] = useState('');
    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    const hasFetchedRef = useRef(false);

    const getOffBoardRequest = async () => {
        try {
            const path = "/getOffBoardRequest";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            if (response.status === 200) {
                setEmployees(response.data);
                setFilteredData(response.data)
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    // Handle 404 status code separately
                    setEmployees([]);
                    setFilteredData([]);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!hasFetchedRef.current) {
            getOffBoardRequest()
            hasFetchedRef.current = true;
        }
    }, []);

    const openModal = (item) => {
        setModalData(item);
        setShowModal(true)
    };

    const handleReset = () => {
        setSelectedOption('');
        setComment('');
        document.getElementById('notifyApproveModal').checked = false;
        document.getElementById('notifyRejectModal').checked = false;
        document.getElementById('notifyComment').value = '';
        document.getElementById('notifyComment').disabled = true;
    };


    const handleOptionChange = (event) => {
        setSelectedOption(event.target.id);
        if (event.target.id === 'notifyRejectModal') {
            // Enable comments field when Reject is selected
            document.getElementById('notifyComment').disabled = false;
        } else {
            // Disable comments field when Approve is selected
            document.getElementById('notifyComment').disabled = true;
        }
    };

    const handleActionSubmit = async (data) => {
        // Prepare payload
        const payload = {
            EmployeeId: data.employeeId,
            EmployeeName: data.employeeName,
            EmployeeEmail: data.employeeEmail,
            Subject: data.subject,
            Reason: data.reason,
            Description: data.description,
            Comments: comment,
            Status: selectedOption === 'notifyApproveModal' ? 'Approved' : 'Rejected'
        };

        try {
            const path = "/offBoardRequest";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.post(URL, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 200) {
                toast.success(`${selectedOption === 'notifyApproveModal' ? 'Approved' : 'Rejected'} successfully!`, {
                    position: toast.POSITION.TOP_CENTER,
                });
                handleReset();
                getOffBoardRequest()
                setTimeout(() => {
                    if (selectedOption === 'notifyApproveModal') {
                        window.location.href = '/offboardemployee';
                    }
                }, 3000);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const sendEmail = (email) => {
        // Excel generation logic
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Employee Separation');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        // Convert the Excel file to a Base64 string
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
            const base64data = reader.result.split(',')[1]; // Get the Base64 string without the prefix

            // Create a downloadable link for the attachment
            const link = document.createElement('a');
            link.href = reader.result;
            const today = new Date();
            const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
            const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
            const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
            const formattedDate = `${year}${month}${day}`;
            link.download = `WW360-Employee_Separation_Report_${formattedDate}.xlsx`;

            // Simulate a click to download the attachment
            link.click();

            // Prepare the mailto link (without attachment)
            const subject = "Employee Separation List";
            const body = 'Please find the attached file.';
            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

            toast.info('The Excel file has been generated and downloaded. Please attach the file manually to your email.', {
                position: toast.POSITION.TOP_CENTER,
            });

            setTimeout(() => {
                window.location.href = mailtoLink;
            }, 3000);

        };
    };

    const shareData = {
        title: 'Employee Separation List',
        text: 'Check out the Employee Separation list',
        url: window.location.href
    };

    const handleShare = async () => {
        try {
            await navigator.share(shareData);
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "EmployeeSeparation");
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        saveAs(blob, `WW360-Employee_Separation_Report_${formattedDate}.xlsx`);
    };

    useEffect(() => {
        // Filter employees based on the search query
        setFilteredData(
            employees.filter(emp =>
                emp.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.employeeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.employeeEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.status.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [searchQuery, employees]);

    const handleRecordsPerPageChange = (event) => {
        setRecordsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);


    const paginate = pageNumber => setCurrentPage(pageNumber);

    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="title mb-2">Employee Separation</h3>
                        </div>
                        <div className="col-auto">
                            <Link to={'/Employees'} className="btn btn-primary mt-lg-0 mt-4">
                                <i className="ri-user-star-line"></i> View All Employees
                            </Link>
                        </div>
                    </div>
                    {employees?.length === 0 ? (
                        <div class="container">
                            <div class="row vh-50 justify-content-center align-items-center">
                                <div class="col-auto">
                                    <div class="no-data-wrapper">
                                        <div class="no-data-found">
                                            <i class="ri-file-damage-fill"></i>
                                            <div class="bounce"><i class="ri-search-eye-line"></i></div>
                                            <p>No Data Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="row align-items-center justify-content-center mb-4">
                                <div className="col-lg-5 col-md-8 col-sm-10">
                                    <div className="container-fluid row-rounded mb-0">
                                        <div className="row px-2 py-0">
                                            <div className="input-group mb-0">
                                                <input
                                                    type="search"
                                                    className="form-control search-input"
                                                    placeholder="Keyword search..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                <div className="input-group-append">
                                                    <button className="btn btn-primary" type="button" title="Search" rel="tooltip"><i className="ri-menu-search-line"></i></button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <small className="form-text text-muted text-center mt-0"><i className="ri-information-2-fill text-info"></i> Allows: Employee ID - Employee Name - Request Date - Status </small>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col">
                                                    <h5 className="title mt-1 mb-0">Summary</h5>
                                                </div>
                                                <div className="col-auto">
                                                    <button
                                                        type="button"
                                                        title="Email"
                                                        rel="tooltip"
                                                        className="btn btn-light btn-circle ml-2"
                                                        onClick={() => sendEmail('')}
                                                    >
                                                        <i className="ri-mail-send-fill"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Share"
                                                        rel="tooltip"
                                                        className="btn btn-light btn-circle ml-2"
                                                        onClick={handleShare}
                                                    >
                                                        <i className="ri-share-fill"></i>
                                                    </button>
                                                    <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={exportToExcel}>
                                                        <i className="ri-export-fill"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="row mt-3">
                                                <div className="col-md-12">
                                                    <div className="table-responsive leave-information">
                                                        <table className="table grid">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>S.No.</th>
                                                                    <th>Employee ID  </th>
                                                                    <th>Employee Name </th>
                                                                    <th>Employee Email  </th>
                                                                    <th>Request Date</th>
                                                                    <th>Status  </th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {currentRecords.map((employee, index) => (
                                                                    <tr key={index}>
                                                                        <td>{index + 1}</td>
                                                                        <td>{employee.employeeId}</td>
                                                                        <td>{employee.employeeName}</td>
                                                                        <td>{employee.employeeEmail}</td>
                                                                        <td>{moment(employee.requestDate).format("DD-MM-YYYY")}</td>
                                                                        <td>
                                                                            <span className={`badge border text-${employee.status === 'Pending' ? 'warning' :
                                                                                employee.status === 'Approved' ? 'success' :
                                                                                    employee.status === 'Submitted' ? 'secondary' :
                                                                                        employee.status === 'Rejected' ? 'light' : ''
                                                                                } status`}>
                                                                                <i className={`ri-${employee.status === 'Pending' ? 'hourglass-fill' :
                                                                                    employee.status === 'Approved' ? 'checkbox-circle-fill' :
                                                                                        employee.status === 'Submitted' ? 'thumb-up-fill' :
                                                                                            employee.status === 'Rejected' ? 'thumb-down-line' : ''
                                                                                    }`}></i>
                                                                                {employee.status}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <span>
                                                                                <span>
                                                                                    <button class="btn btn-link text-info btn-action" type="button" data-target="#listViewModal" data-toggle="modal" title="List View" rel="List View" onClick={() => openModal(employee)}>
                                                                                        <i class="ri-layout-horizontal-line"></i>
                                                                                    </button>
                                                                                </span>
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="container-fluid">
                                                        <div className="row border-top pt-3">
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Records</p>
                                                            </div>
                                                            <div className="col-1 record-drop">
                                                                <select className="form-control" value={recordsPerPage} onChange={handleRecordsPerPageChange}>
                                                                    <option value="5">5</option>
                                                                    <option value="10">10</option>
                                                                    <option value="15">15</option>
                                                                    <option value="20">20</option>
                                                                    <option value="25">25</option>
                                                                </select>
                                                            </div>
                                                            <div className="col">
                                                                <nav>
                                                                    <ul className="pagination justify-content-center">
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-double-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        {Array.from({ length: totalPages }, (_, index) => (
                                                                            <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                                    {index + 1}
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                                                <i className="ri-arrow-right-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(totalPages)}>
                                                                                <i className="ri-arrow-right-double-fill"></i>
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            </div>
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {modalData && showModal && (
                <div class="modal fade" id="listViewModal" tabindex="-1" role="dialog" aria-labelledby="listViewModalLabel" aria-hidden="true">
                    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div class="modal-content">
                            <div class="modal-header py-2">
                                <h6 class="modal-title" id="listViewModalLabel">Employee Separation</h6>
                                <button class="close" type="button" data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true"><i class="ri-close-circle-fill font-size-20"></i></span>
                                </button>
                            </div>
                            <div class="modal-body py-0">
                                <div class="row mb-2">
                                    <div class="col-4">
                                        <small class="form-text text-muted mt-2 text-right">Employee ID</small>
                                    </div>
                                    <div class="col-8">
                                        <input type="text" class="form-control-plaintext" value={modalData.employeeId} readonly />
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-4">
                                        <small class="form-text text-muted mt-2 text-right">Employee Name</small>
                                    </div>
                                    <div class="col-8">
                                        <input type="text" class="form-control-plaintext" value={modalData.employeeName} readonly />
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-4">
                                        <small class="form-text text-muted mt-2 text-right">Employee Email</small>
                                    </div>
                                    <div class="col-8">
                                        <input type="text" class="form-control-plaintext" value={modalData.employeeEmail} readonly />
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-4">
                                        <small class="form-text text-muted mt-2 text-right">Reason</small>
                                    </div>
                                    <div class="col-8">
                                        <textarea class="form-control-plaintext" readonly>{modalData.reason}</textarea>
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-4">
                                        <small class="form-text text-muted mt-2 text-right">Description</small>
                                    </div>
                                    <div class="col-8">
                                        <textarea class="form-control-plaintext" readonly>{modalData.description}</textarea>
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-4">
                                        <small class="form-text text-muted mt-2 text-right">Request Date</small>
                                    </div>
                                    <div class="col-8">
                                        <input type="text" class="form-control-plaintext" value={moment(modalData.requestDate).format("DD-MM-YYYY")} readonly />
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-4">
                                        <small class="form-text text-muted mt-0 text-right">Status</small>
                                    </div>
                                    <div class="col-2">
                                        {modalData.status === 'Pending' && (
                                            <span class="badge border border-warning text-warning status">
                                                <i class="ri-hourglass-fill"></i> Pending
                                            </span>
                                        )}
                                        {modalData.status === 'Submitted' && (
                                            <span class="badge border border-secondary text-secondary status">
                                                <i class="ri-thumb-up-fill"></i> Submitted
                                            </span>
                                        )}
                                        {modalData.status === 'Rejected' && (
                                            <span class="badge border border-light text-light status">
                                                <i class="ri-thumb-down-line"></i> Rejected
                                            </span>
                                        )}
                                        {modalData.status === 'Approved' && (
                                            <span class="badge border bg-success text-white status">
                                                <i class="ri-thumb-up-fill"></i> Approved
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div class="row mb-2">
                                    <div class="col-4">
                                        <small class="form-text text-muted mt-2 text-right">Comments</small>
                                    </div>
                                    <div class="col-8">
                                        <textarea class="form-control-plaintext" readonly>{modalData.comments}</textarea>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-content">
                                <div className="modal-body border-top pb-0">
                                    <div className="row justify-content-center">
                                        <div className="form-group col-3 text-center">
                                            <div className="custom-control custom-radio">
                                                <input
                                                    type="radio"
                                                    className="custom-control-input"
                                                    name="updateClaimModal"
                                                    id="notifyApproveModal"
                                                    onChange={handleOptionChange}
                                                />
                                                <label className="custom-control-label control-label-1" htmlFor="notifyApproveModal">Approve</label>
                                            </div>
                                        </div>
                                        <div className="form-group col-3 text-center">
                                            <div className="custom-control custom-radio">
                                                <input
                                                    type="radio"
                                                    className="custom-control-input"
                                                    name="updateClaimModal"
                                                    id="notifyRejectModal"
                                                    onChange={handleOptionChange}
                                                />
                                                <label className="custom-control-label control-label-1" htmlFor="notifyRejectModal">Reject</label>
                                            </div>
                                        </div>
                                        <div className="form-group col-8">
                                            <label htmlFor="notifyComment" className="required">Comment <small>(If Reject)</small></label>
                                            <textarea
                                                className="form-control"
                                                id="notifyComment"
                                                rows="3"
                                                cols="5"
                                                disabled
                                                onChange={(e) => setComment(e.target.value)}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer py-2 justify-content-center">
                                    <button className="btn btn-light" type="button" onClick={handleReset}>
                                        <i className="ri-arrow-left-circle-line"></i> Reset
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={() => handleActionSubmit(modalData)}>
                                        Submit <i className="ri-checkbox-circle-line"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default EmployeeSeparation;

